
import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";
import { Client } from "@stomp/stompjs";
import { API_URL } from "../../Constants";

// 👇 Yahan apne logos ko import karein (Path apne hisaab se adjust kar lena)
import FullLogo from "../../assets/loginlogo.png"; 
import SmallLogo from "../../assets/sidebarlogo.png"; 

import {
  FaSignOutAlt,
  FaTachometerAlt,
  FaStickyNote,
  FaUsers,
  FaTasks,
  FaBell,
  FaCalendarAlt,
  FaUserTie,
  FaUserCheck,
  FaFileInvoice,
  FaCog,
  FaChevronDown,
  FaChevronRight,
  FaChevronLeft,
  FaUserPlus,
  FaBan,
  FaClipboardList,
  FaUserFriends,
  FaBoxTissue,
  FaWarehouse,
  FaWhatsapp,
  FaExclamationCircle,
  FaCubes,
  FaClone,
  FaFileInvoiceDollar,
  FaUserClock,
  FaAddressBook,
} from "react-icons/fa";

const WA_WS_URL = "wss://waws.deetyasoft.com/sendMessage";

// ---- WhatsApp persistence helpers (shared localStorage keys with Chat.js) ----
//   wa_unread_<companyId>   : { [number]: unreadCount }
//   wa_last_<companyId>     : { [number]: { text, type, sender, timestamp } }
//   wa_lastread_<companyId> : { [number]: ISO time up to which the chat was read }
const waReadStore = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "{}") || {};
  } catch (e) {
    return {};
  }
};
const waWriteStore = (key, value) => localStorage.setItem(key, JSON.stringify(value));
const waToMs = (v) => {
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? 0 : t;
};
const waIsReaction = (m) =>
  m.type === "reaction" ||
  (m.type === "text" && typeof m.text === "string" && m.text.startsWith("Reacted with"));
const WA_MEDIA_LABELS = { image: "📷 Photo", video: "🎥 Video", audio: "🎵 Audio", document: "📄 Document" };
const waPreviewOf = (m) => m.message || m.text || WA_MEDIA_LABELS[m.type] || (m.type && m.type !== "text" ? `Sent a ${m.type}` : "");

const Sidebar = ({ mode = "account", isOpen, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  
  // Custom Hover Flyout State for Collapsed Mode
  const [hoverData, setHoverData] = useState(null);
  const hoverTimeoutRef = useRef(null);
  
  // Total Unread Notification State
  const [totalUnread, setTotalUnread] = useState(0);

  const [openDropdowns, setOpenDropdowns] = useState(() => {
    const savedStates = localStorage.getItem("sidebarDropdownStates");
    return savedStates ? JSON.parse(savedStates) : {};
  });
  
  const [permissions, setPermissions] = useState(() => {
    const savedPermissions = localStorage.getItem("userPermissions");
    return savedPermissions ? JSON.parse(savedPermissions) : {};
  });
  
  const [loading, setLoading] = useState(!Object.keys(permissions).length);

  // Sync Global Unread Messages Count
  useEffect(() => {
    const calculateTotalUnread = () => {
      if (!user) return;
      const companyId = user?.accountType === "COMPANY" ? user?.id : user?.companyId;
      try {
        const stored = JSON.parse(localStorage.getItem(`wa_unread_${companyId}`) || "{}");
        const total = Object.values(stored).reduce((sum, count) => sum + count, 0);
        setTotalUnread(total);
      } catch (e) {
        setTotalUnread(0);
      }
    };

    calculateTotalUnread();
    window.addEventListener("wa_unread_changed", calculateTotalUnread);
    return () => window.removeEventListener("wa_unread_changed", calculateTotalUnread);
  }, [user]);

  // Global WhatsApp listener: lives in the Sidebar (mounted on every page after login) so
  // unread counts and last messages are tracked no matter which page is open.
  const canUseWhatsapp = permissions?.whatsapp === true;
  const waCompanyId = user?.accountType === "COMPANY" ? user?.id : user?.companyId;

  useEffect(() => {
    if (!user?.id || !waCompanyId || !canUseWhatsapp) return;

    const UNREAD_KEY = `wa_unread_${waCompanyId}`;
    const LAST_KEY = `wa_last_${waCompanyId}`;
    const READ_KEY = `wa_lastread_${waCompanyId}`;
    const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

    let cancelled = false;
    let refreshing = false;
    let client = null;
    let apiKey = "";
    let pollTimer = null;
    const subs = {};
    const knownNumbers = new Set();
    const seenIds = new Set();
    const reconciled = {};

    const setUnread = (number, count) => {
      const map = waReadStore(UNREAD_KEY);
      if ((map[number] || 0) === count) return;
      if (count <= 0) delete map[number];
      else map[number] = count;
      waWriteStore(UNREAD_KEY, map);
      window.dispatchEvent(new Event("wa_unread_changed"));
    };

    const markRead = (number, ts) => {
      const map = waReadStore(READ_KEY);
      const next = waToMs(ts) || Date.now();
      if (next > waToMs(map[number])) {
        map[number] = new Date(next).toISOString();
        waWriteStore(READ_KEY, map);
      }
      setUnread(number, 0);
    };

    const saveLast = (number, entry) => {
      const map = waReadStore(LAST_KEY);
      if (map[number] && waToMs(map[number].timestamp) > waToMs(entry.timestamp)) return;
      map[number] = entry;
      waWriteStore(LAST_KEY, map);
      window.dispatchEvent(new Event("wa_last_changed"));
    };

    // Live message from the websocket
    const handleIncoming = (number, rawBody) => {
      let msg;
      try {
        msg = JSON.parse(rawBody);
      } catch (e) {
        return;
      }
      if (!msg || waIsReaction(msg)) return;

      const incoming = msg.direction === "INCOMING";
      const ts = msg.timestamp || new Date().toISOString();

      saveLast(number, {
        text: waPreviewOf(msg),
        type: msg.type || "text",
        sender: incoming ? "them" : "me",
        timestamp: ts,
      });
      if (!incoming) return;

      // De-duplicate (the same wamid can be re-delivered with status changes)
      const id = msg.wamid || msg.messageId;
      if (id) {
        if (seenIds.has(id)) return;
        seenIds.add(id);
        if (seenIds.size > 500) seenIds.delete(seenIds.values().next().value);
      }

      // Chat page has this contact open -> it is being read right now
      if (window.__waActiveContact === number) {
        markRead(number, ts);
        return;
      }
      setUnread(number, (waReadStore(UNREAD_KEY)[number] || 0) + 1);
    };

    const subscribeNumber = (number) => {
      if (!client || !client.connected || !apiKey || subs[number]) return;
      subs[number] = client.subscribe(`/topic/chatMessages/${apiKey}/${number}`, (frame) =>
        handleIncoming(number, frame.body)
      );
    };

    const fetchAllSenders = async () => {
      const items = [];
      let page = 0;
      let hasMore = true;
      while (hasMore && page < 50) {
        const res = await fetch(`${API_URL}/api/whatsapp-chat/unique-senders?page=${page}&size=100`, { headers });
        if (!res.ok) return null;
        const data = await res.json();
        items.push(...(data.data || []));
        hasMore = !!data.hasMore;
        page = data.nextPage;
      }
      return items.filter((i) => i && i.number);
    };

    // Catch-up from persisted history: handles messages that arrived while logged out /
    // while the websocket was down, and fills the "last message" cache.
    const syncContact = async (item) => {
      const number = String(item.number);
      const itemMs = waToMs(item.time);
      const key = String(item.time || "");

      // First time we see this contact: set the "read up to" baseline
      const readMap = waReadStore(READ_KEY);
      if (!readMap[number]) {
        readMap[number] = new Date(itemMs || Date.now()).toISOString();
        waWriteStore(READ_KEY, readMap);
        const backendUnread = Number(item.unreadCount) || 0;
        if (backendUnread > (waReadStore(UNREAD_KEY)[number] || 0) && window.__waActiveContact !== number) {
          setUnread(number, backendUnread);
        }
      }

      if (reconciled[number] === key) return;

      const lastRead = waToMs(waReadStore(READ_KEY)[number]);
      const cachedLast = waReadStore(LAST_KEY)[number];
      const hasNewer = itemMs > lastRead;
      const lastStale = !cachedLast || waToMs(cachedLast.timestamp) < itemMs;
      if (!hasNewer && !lastStale) {
        reconciled[number] = key;
        return;
      }

      try {
        const res = await fetch(
          `${API_URL}/api/whatsapp-chat/chat?sender=${encodeURIComponent(number)}&page=0&size=50`,
          { headers }
        );
        if (!res.ok) return;
        const body = await res.json();
        const list = (body.data || [])
          .filter((m) => m && !waIsReaction(m))
          .map((m) => ({ ...m, _ts: waToMs(m.timestamp || m.time) }))
          .sort((a, b) => b._ts - a._ts);

        if (list.length) {
          const newest = list[0];
          saveLast(number, {
            text: waPreviewOf(newest),
            type: newest.type || "text",
            sender: newest.direction === "INCOMING" ? "them" : "me",
            timestamp: newest.timestamp || newest.time || new Date().toISOString(),
          });

          if (window.__waActiveContact === number) {
            markRead(number, newest._ts);
          } else if (hasNewer) {
            const count = list.filter((m) => m.direction === "INCOMING" && m._ts > lastRead).length;
            if (count > (waReadStore(UNREAD_KEY)[number] || 0)) setUnread(number, count);
          }
        }
        reconciled[number] = key;
      } catch (e) {
        console.error("WhatsApp history sync failed:", e);
      }
    };

    const refreshSenders = async () => {
      if (refreshing || cancelled) return;
      refreshing = true;
      try {
        const items = await fetchAllSenders();
        if (cancelled || !items) return;
        items.forEach((it) => {
          const n = String(it.number);
          knownNumbers.add(n);
          subscribeNumber(n);
        });
        for (let i = 0; i < items.length; i += 5) {
          if (cancelled) return;
          await Promise.all(items.slice(i, i + 5).map(syncContact));
        }
      } catch (e) {
        console.error("WhatsApp sender refresh failed:", e);
      } finally {
        refreshing = false;
      }
    };

    const init = async () => {
      try {
        const res = await fetch(`${API_URL}/api/whatsappConfig/${waCompanyId}`, { headers });
        const result = await res.json();
        if (cancelled || !res.ok || result.status !== "success" || !result.data?.apiKey) return;
        apiKey = result.data.apiKey;
      } catch (e) {
        return;
      }

      client = new Client({
        brokerURL: WA_WS_URL,
        reconnectDelay: 5000,
        heartbeatIncoming: 10000,
        heartbeatOutgoing: 10000,
        onConnect: () => {
          Object.keys(subs).forEach((k) => delete subs[k]);
          knownNumbers.forEach(subscribeNumber);
          refreshSenders(); // catch up on anything missed while disconnected
        },
      });
      client.activate();

      refreshSenders();
      // picks up brand-new contacts (new numbers) and subscribes to them
      pollTimer = setInterval(refreshSenders, 60000);
    };

    init();

    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
      Object.values(subs).forEach((s) => {
        try { s.unsubscribe(); } catch (_) {}
      });
      if (client) client.deactivate();
    };
  }, [user?.id, waCompanyId, canUseWhatsapp]);

  useEffect(() => {
    if (user?.accountType === "SUPER_SUPER_ADMIN") {
      setPermissions({
        permissionId: null, accountCreatedById: user.id, createAccount: true,
        viewAccount: true, dashboard: true, leads: true, task: true,
        leadTask: true, issues: true, reminder: true, whatsapp: true,
        meeting: true, todo: true, notes: true, calendar: true,
        invoice: true, hr: true, customer: true, greetings: true,
        whatsappIntegration: true, generalsettings: true, attributes: true,
        leadStatusAttributes: true, leadSourceAttributes: true,
        projectNameAttributes: true, taskLabelAttributes: true,
        taskStatusAttributes: true, issuesStatusAttributes: true,
        issuesType: true, environmentAttributes: true, severityAttributes: true,
        templates: true, smsTemplates: true, emailTemplates: true,
        greetingTemplates: true, clickToCall: true, automation: true,
        emailAutomation: true, whatsappAutomation: true, ivrIntegration: true,
        smsAutomation: true, nnAcresConfig: true,
      });
    }
  }, [user]);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (Object.keys(permissions).length || !user?.id) return;
      try {
        setLoading(true);
        const url = (user?.accountType === "COMPANY" || user?.accountType === "SUPER_ADMIN" || user?.accountType === "RESELLER")
            ? `${API_URL}/api/user/details/${user.id}`
            : `${API_URL}/api/companyuser/details/${user.id}`;

        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });

        const fetchedPermissions = response.data.permission[0] || {};
        setPermissions(fetchedPermissions);
        localStorage.setItem("userPermissions", JSON.stringify(fetchedPermissions));
      } catch (error) {
        console.error("Error fetching permissions:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPermissions();
  }, [user?.id, permissions, user?.accountType]);

  useEffect(() => {
    localStorage.setItem("sidebarDropdownStates", JSON.stringify(openDropdowns));
  }, [openDropdowns]);

  useEffect(() => {
    const isAccountPath = accountMenuItems.some(item => 
      (item.path && (location.pathname === item.path || (item.path !== "/" && location.pathname.startsWith(item.path)))) || 
      (item.isDropdown && item.subItems && item.subItems.some(sub => location.pathname === sub.path || (sub.path !== "/" && location.pathname.startsWith(sub.path))))
    );
    
    const menuItems = isAccountPath ? accountMenuItems : leadMenuItems;
    const newOpenStates = { ...openDropdowns };

    menuItems.forEach((item) => {
      if (item.isDropdown && item.subItems) {
        const shouldOpen = item.subItems.some(
          (subItem) =>
            location.pathname === subItem.path ||
            (subItem.path !== "/" && location.pathname.startsWith(subItem.path))
        );
        if (shouldOpen) newOpenStates[item.menuKey] = true;
      }
    });
    setOpenDropdowns(newOpenStates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const confirmLogout = async () => {
    try {
      await fetch(`${API_URL}/api/session/manualLogout/${user?.id}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
    } catch (error) {}

    localStorage.removeItem("token");
    localStorage.removeItem("userPermissions");
    localStorage.removeItem("sidebarDropdownStates");
    localStorage.removeItem("profileImage");
    localStorage.setItem("logout", Date.now());
    navigate("/");
    window.location.reload();
  };

  const toggleSidebar = () => onToggle(!isOpen);
  const closeSidebar = () => { if (window.innerWidth < 1024) onToggle(false); };

  // Below 1024px the sidebar is a slide-over drawer: make sure it starts closed
  // and let a left-swipe close it. Desktop (>=1024px) is untouched.
  const swipeStartRef = useRef(null);
  useEffect(() => {
    if (window.innerWidth < 1024 && isOpen) onToggle(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleDrawerTouchStart = (e) => {
    const t = e.touches[0];
    swipeStartRef.current = { x: t.clientX, y: t.clientY };
  };
  const handleDrawerTouchEnd = (e) => {
    const start = swipeStartRef.current;
    swipeStartRef.current = null;
    if (!start || window.innerWidth >= 1024 || !isOpen) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (dx < -60 && Math.abs(dx) > Math.abs(dy) * 1.5) onToggle(false);
  };

  const toggleDropdown = (menuKey) => {
    setOpenDropdowns((prev) => ({ ...prev, [menuKey]: !prev[menuKey] }));
  };

  const hasPermission = (permissionKey) => permissions[permissionKey] === true;

  const filterMenuItems = (items) => {
    return items.filter((item) => {
      if (item.permissionKey && !hasPermission(item.permissionKey)) return false;
      if (item.subItems) {
        item.subItems = filterMenuItems(item.subItems);
        return item.subItems.length > 0;
      }
      return true;
    });
  };

  const isActive = (path) => {
    return location.pathname === path || (path !== "/" && location.pathname.startsWith(path));
  };

  const handleMouseEnter = (e, item) => {
    if (isOpen) return;
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    
    const rect = e.currentTarget.getBoundingClientRect();
    setHoverData({
      item,
      top: rect.top,
    });
  };

  const handleMouseLeave = () => {
    if (isOpen) return;
    hoverTimeoutRef.current = setTimeout(() => {
      setHoverData(null);
    }, 150);
  };

  const handleFlyoutEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
  };

  const renderMenu = (items, level = 0) => (
    <ul className={`${level === 0 ? "space-y-1.5" : "ml-4 mt-1 space-y-0.5 w-[92%]"}`}>
      {items.map((item, index) => (
        <li 
          key={index} 
          className="relative"
          onMouseEnter={(e) => level === 0 && handleMouseEnter(e, item)}
          onMouseLeave={() => level === 0 && handleMouseLeave()}
        >
          {item.isDropdown ? (
            <div>
              <button
                className={`flex items-center w-full py-3 lg:py-2.5 rounded-md transition-all duration-200 ${
                  isActive(item.path)
                    ? "bg-indigo-50 text-indigo-700 border border-indigo-200"
                    : "text-gray-700 hover:bg-gray-100 hover:text-indigo-600"
                } ${!isOpen && level === 0 ? "justify-center px-0" : "justify-between px-3"}`}
                onClick={() => {
                  if (isOpen) toggleDropdown(item.menuKey);
                  else onToggle(true);
                }}
              >
                <div className={`flex items-center ${!isOpen && level === 0 ? "justify-center" : "gap-3"} w-full`}>
                  {level === 0 && (
                    <div className="relative flex items-center justify-center min-w-[24px]">
                      <span className="text-xl">{item.icon}</span>
                      {item.menuKey === "whatsappGroup" && totalUnread > 0 && !isOpen && (
                        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#00a884] rounded-full border border-white"></span>
                      )}
                    </div>
                  )}
                  {(isOpen || level > 0) && (
                    <span className="flex-1 flex items-center justify-between pr-2">
                      <span className={`font-medium whitespace-normal lg:whitespace-nowrap ${level > 0 ? "text-[13px]" : "text-sm"}`}>{item.name}</span>
                      {item.menuKey === "whatsappGroup" && totalUnread > 0 && (
                        <span className="bg-[#00a884] text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm ml-2">
                          {totalUnread}
                        </span>
                      )}
                    </span>
                  )}
                </div>
                {(isOpen || level > 0) && (
                  <FaChevronDown className={`transition-transform duration-200 text-xs shrink-0 ${openDropdowns[item.menuKey] ? "rotate-180" : "rotate-0"}`} />
                )}
              </button>
              {isOpen && openDropdowns[item.menuKey] && (
                <div className="overflow-hidden">
                  {renderMenu(item.subItems, level + 1)}
                </div>
              )}
            </div>
          ) : (
            <Link
              to={item.path}
              className={`flex items-center rounded-md transition-all duration-200 ${
                level > 0 ? "py-2.5 lg:py-1.5 text-[13px]" : "py-3 lg:py-2.5"
              } ${
                isActive(item.path)
                  ? "bg-[#673AB7] text-white shadow-sm"
                  : "text-gray-800 hover:bg-indigo-50 hover:text-[#673AB7]"
              } ${!isOpen && level === 0 ? "justify-center px-0" : "gap-3 px-3"}`}
              onClick={closeSidebar}
            >
              {level > 0 && <span className="text-xs opacity-60">•</span>}
              {level === 0 && (
                <div className="relative flex items-center justify-center min-w-[24px]">
                  <span className="text-xl">{item.icon}</span>
                  {item.name === "Chat" && totalUnread > 0 && !isOpen && (
                    <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#00a884] rounded-full border border-white"></span>
                  )}
                </div>
              )}
              {(isOpen || level > 0) && (
                <span className="flex-1 flex items-center justify-between">
                  <span className={`font-medium whitespace-normal lg:whitespace-nowrap ${level === 0 ? "text-sm" : ""}`}>{item.name}</span>
                  {item.name === "Chat" && totalUnread > 0 && (
                    <span className="bg-[#00a884] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm ml-2">
                      {totalUnread}
                    </span>
                  )}
                </span>
              )}
            </Link>
          )}
        </li>
      ))}
    </ul>
  );

  const accountMenuItems = [
    { name: "Create Account", path: "/createAccount", icon: <FaUserPlus />, permissionKey: "createAccount" },
    { name: "View Accounts", path: "/viewAccount", icon: <FaUsers />, permissionKey: "viewAccount" },
    { name: "Account Statement", path: "/accountStatement", icon: <FaUsers />, permissionKey: "generalsettings" },
    { name: "General Settings", path: "/general-settings", icon: <FaCog />, permissionKey: "generalsettings", isDropdown: true, menuKey: "generalsettings",
      subItems: [
        { name: "Company Setting", path: "/webSetting", permissionKey: "generalsettings" },
        { name: "Google Form", path: "/googleForm", permissionKey: "generalsettings" },
        { name: "Workflow Automation", path: "/workflow-automation", permissionKey: "generalsettings" },
        { name: "Ivr Integration", path: "/ivrIntegration", permissionKey: "ivrIntegration" },
        { name: "Key Integration", path: "/keyIntegration", permissionKey: "emailAutomation" },
        { name: "Click To Call Integration", path: "/clickToCall", permissionKey: "clickToCall" },
        { name: "Facebook Integration", path: "/facebookIntegration", permissionKey: "emailAutomation" },
        { name: "AI Configuration", path: "/ai-configuration", permissionKey: "ivrIntegration" },
        { name: "SMS Configuration", path: "/smsConfig", permissionKey: "smsAutomation" },
        { name: "Email Configuration", path: "/emailConfig", permissionKey: "emailAutomation" },
        { name: "Whatsapp Configuration", path: "/whatsappConfig", permissionKey: "whatsappIntegration" },
        { name: "VideoCall Configuration", path: "/video-config", permissionKey: "generalsettings" },
         ...(user?.accountType === "SUPER_SUPER_ADMIN"
          ? [{ name: "Support Settings", path: "/support-settings", permissionKey: "generalsettings" }]
          : []),
      ],
    },
  ];

  const leadMenuItems = [
    { name: "Dashboard", path: "/dashboard", icon: <FaTachometerAlt />, permissionKey: "dashboard" },
    { name: "Lead", path: "/lead/lead", icon: <FaUsers />, permissionKey: "leads" },
    { name: "Contact", path: "/contacts", icon: <FaAddressBook />, permissionKey: "leads" },
    { name: "Lead Tasks", path: "/lead-tasks", icon: <FaTasks />, permissionKey: "leadTask" },
    { name: "Tasks", path: "/tasks", icon: <FaTasks />, permissionKey: "task" },
    { name: "Issues", path: "/issues", icon: <FaExclamationCircle />, permissionKey: "issues" },
    { name: "WhatsApp", icon: <FaWhatsapp />, isDropdown: true, menuKey: "whatsappGroup",
      subItems: [
        ...(hasPermission("whatsapp") ? [
              { name: "Chat", path: "/chat", permissionKey: "whatsapp" },
              { name: "Send SMS", path: "/whatsappSms", permissionKey: "whatsapp" },
            ] : [{ name: "NO Permission", path: "#" }]),
      ],
    },
    { name: "Reminder", path: "/reminder", icon: <FaBell />, permissionKey: "reminder" },
    { name: "Meeting", path: "/meeting", icon: <FaUserFriends />, permissionKey: "meeting" },
    { name: "To-Do", icon: <FaClipboardList />, path: "/todo", permissionKey: "todo" },
    { name: "Calendar", path: "/calendar", icon: <FaCalendarAlt />, permissionKey: "calendar" },
    { name: "Customer", path: "/customer", icon: <FaUserTie />, permissionKey: "customer" },
    { name: "Greeting", path: "/greeting", icon: <FaUserCheck />, permissionKey: "greetings" },
    { name: "Notes", path: "/note", icon: <FaStickyNote />, permissionKey: "notes" },
    { name: "Attributes", icon: <FaCubes />, isDropdown: true, menuKey: "attributes", permissionKey: "attributes",
      subItems: [
        { name: "Lead Status", path: "/leadStatus", permissionKey: "leadStatusAttributes" },
        { name: "Lead Source", path: "/leadSource", permissionKey: "leadSourceAttributes" },
        { name: "Lead Config", path: "/leadconfig", permissionKey: "leadSourceAttributes" },
        { name: "Project Name", path: "/leadLabel", permissionKey: "projectNameAttributes" },
        { name: "Task Label", path: "/taskLabel", permissionKey: "taskLabelAttributes" },
        { name: "Task Status", path: "/taskStatus", permissionKey: "taskStatusAttributes" },
        { name: "Issue Status", path: "/issueStatus", permissionKey: "issuesStatusAttributes" },
        { name: "Issue Type", path: "/issueType", permissionKey: "issuesType" },
        { name: "Severity", path: "/severityAttributes", permissionKey: "severityAttributes" },
        { name: "Environment", path: "/environmentAttributes", permissionKey: "environmentAttributes" },
        { name: "Customer Tag", path: "/customerTag", permissionKey: "customerTagAttributes" },
        { name: "Lead Follow-Up", path: "/leadFollowup", permissionKey: "leadFollowupAttributes" },
        { name: "Appointment Config", path: "/appointment-config", permissionKey: "leadStatusAttributes" },
      ],
    },
    { name: "Templates", icon: <FaClone />, isDropdown: true, menuKey: "templates", permissionKey: "templates",
      subItems: [
        { name: "Whatsapp Templates", path: "/whatsappTemplates", permissionKey: "whatsappTemplates" },
        { name: "SMS Templates", path: "/smsTemplates", permissionKey: "smsTemplates" },
        { name: "Email Templates", path: "/emailTemplates", permissionKey: "emailTemplates" },
        { name: "Greeting Templates", path: "/greetingTemplates", permissionKey: "greetingTemplates" },
      ],
    },
    { name: "Blacklist", path: "/blacklist", icon: <FaBan />, permissionKey: "blackList" },
    { name: "Invoice", icon: <FaFileInvoiceDollar />, isDropdown: true, menuKey: "invoice", permissionKey: "invoice",
      subItems: [
        { name: "Create Invoice", path: "/invoice" },
        { name: "View Invoice", path: "/viewInvoice" },
      ],
    },
    { name: "Attendance Report", icon: <FaUserClock />, isDropdown: true, menuKey: "attendanceReport", permissionKey: "hr",
      subItems: [
        { name: "Attendance", path: "/attendance" },
        { name: "Leave", path: "/leave" },
      ],
    },
    { name: "Working Policy", path: "/workingPolicy", icon: <FaWarehouse /> },
  ];

  if (loading) return null;

  const isAccountPage = accountMenuItems.some(item => 
    (item.path && (location.pathname === item.path || (item.path !== "/" && location.pathname.startsWith(item.path)))) || 
    (item.isDropdown && item.subItems && item.subItems.some(sub => location.pathname === sub.path || (sub.path !== "/" && location.pathname.startsWith(sub.path))))
  );

  const activeMode = isAccountPage ? "account" : "lead";
  const currentMenuItems = activeMode === "account" ? accountMenuItems : leadMenuItems;
  const filteredMenuItems = filterMenuItems(currentMenuItems);

  return (
    <>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: transparent; border-radius: 10px; transition: background 0.3s ease; }
        .sidebar-container:hover .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; }
        .custom-scrollbar:hover::-webkit-scrollbar-thumb { background: #94a3b8; }
      `}</style>

      <div className="flex h-full dark:bg-gray-900 relative">
        <div
          className={`sidebar-container fixed inset-y-0 left-0 bg-gradient-to-b from-indigo-100 via-white to-purple-100 border-r border-indigo-200 text-gray-800 transform transition-all duration-300 ease-in-out shadow-lg z-[60] lg:z-50 flex flex-col
            ${isOpen ? "translate-x-0 w-[82vw] max-w-[18rem] lg:w-64 lg:max-w-none" : "-translate-x-full w-[82vw] max-w-[18rem] lg:translate-x-0 lg:w-20 lg:max-w-none"}
          `}
          onTouchStart={handleDrawerTouchStart}
          onTouchEnd={handleDrawerTouchEnd}
        >
          <button
            onClick={toggleSidebar}
            className="hidden lg:flex absolute top-1/2 -translate-y-1/2 -right-[19px] w-[20px] h-[60px] bg-white border-y border-r border-indigo-200 rounded-r-full items-center justify-center shadow-md hover:bg-indigo-50 hover:text-[#673AB7] z-[100] transition-all duration-200 cursor-pointer text-indigo-400 group"
            title={isOpen ? "Collapse Sidebar" : "Expand Sidebar"}
          >
            {isOpen ? (
              <FaChevronLeft size={12} className="group-hover:-translate-x-0.5 transition-transform" />
            ) : (
              <FaChevronRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
            )}
          </button>

          <div className="h-full flex flex-col p-2 w-full pt-4">
            <button aria-label="Close menu" className="lg:hidden absolute top-3 right-3 z-10 flex items-center justify-center w-10 h-10 bg-white/80 border border-indigo-100 text-gray-600 text-lg shadow-sm hover:bg-red-500 hover:text-white rounded-full transition active:scale-95" onClick={closeSidebar}>
              ✕
            </button>

            <div className="shrink-0 flex items-center justify-start lg:justify-center pl-2 pr-14 lg:px-0 border-b border-indigo-200/50 pb-4 mb-2 min-h-[64px]">
              {isOpen ? (
                <img 
                  src={FullLogo} 
                  alt="Full Brand Logo" 
                  className="object-contain transition-all duration-300 h-10 w-auto max-w-[150px]"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = document.getElementById('logo-fallback-text');
                    if(fallback) fallback.style.display = 'flex';
                  }}
                />
              ) : (
                <img 
                  src={SmallLogo} 
                  alt="Small Icon Logo" 
                  className="object-contain transition-all duration-300 h-8 w-8"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = document.getElementById('logo-fallback-text');
                    if(fallback) fallback.style.display = 'flex';
                  }}
                />
              )}
              <div 
                id="logo-fallback-text"
                style={{display: 'none'}} 
                className="items-center justify-center font-bold text-indigo-700 bg-indigo-50 rounded-lg w-full h-10"
              >
                {isOpen ? "BRAND LOGO" : "B"}
              </div>
            </div>

            <div 
              className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar px-1"
              onScroll={() => setHoverData(null)}
            >
              {renderMenu(filteredMenuItems)}
            </div>

            <div className="shrink-0 mt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] px-1 pt-2 border-t border-indigo-100">
              <motion.button
                onClick={() => setShowLogoutModal(true)}
                className={`flex items-center gap-2 w-full py-3 lg:py-2.5 rounded-md bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-all duration-200 ${isOpen ? "px-4 justify-center" : "px-0 justify-center"}`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                title={!isOpen ? "Logout" : ""}
              >
                <FaSignOutAlt className="text-xl min-w-[24px]" />
                {isOpen && <span className="font-semibold">Logout</span>}
              </motion.button>
            </div>
          </div>
        </div>

        {!isOpen && hoverData && (
          <div
            className="fixed z-[1000] left-[80px]"
            style={{ top: hoverData.top }}
            onMouseEnter={handleFlyoutEnter}
            onMouseLeave={handleMouseLeave}
          >
            {hoverData.item.isDropdown ? (
              <div 
                className="ml-3 w-56 bg-white shadow-2xl rounded-lg border border-indigo-100 flex flex-col animate-fadeIn"
                style={{ maxHeight: `calc(100vh - ${hoverData.top}px - 20px)` }}
              >
                <div className="px-4 py-3 font-semibold text-gray-800 bg-indigo-50/50 border-b border-indigo-100 shrink-0 text-sm">
                  {hoverData.item.name}
                </div>
                <ul className="py-2 overflow-y-auto custom-scrollbar">
                  {hoverData.item.subItems.map((sub, subIndex) => (
                    <li key={subIndex}>
                      <Link
                        to={sub.path}
                        className="flex items-center justify-between px-4 py-2 text-[13px] text-gray-600 hover:bg-indigo-50 hover:text-[#673AB7] transition-colors"
                      >
                        <span>{sub.name}</span>
                        {sub.name === "Chat" && totalUnread > 0 && (
                          <span className="bg-[#00a884] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                            {totalUnread}
                          </span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="ml-3 px-3 py-2 bg-gray-800 text-white text-sm font-medium rounded-md shadow-lg flex items-center animate-fadeIn">
                <div className="absolute left-1 border-[6px] border-transparent border-r-gray-800"></div>
                {hoverData.item.name}
              </div>
            )}
          </div>
        )}

        {isOpen && <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[55] lg:z-40 lg:hidden" onClick={closeSidebar}></div>}
        {/* The floating purple hamburger was removed: the Navbar already has a menu button and the two overlapped on phones. */}

        {showLogoutModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm">
            <div className="w-[calc(100vw-2rem)] max-w-[20rem] rounded-xl bg-black/20 backdrop-blur-xl shadow-xl p-6 text-center animate-fadeIn">
              <div className="flex justify-center mb-3">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-100 text-red-500">
                  <FaSignOutAlt />
                </div>
              </div>
              <h2 className="text-lg font-bold text-white mb-2">Confirm Logout</h2>
              <p className="text-sm text-white mb-6">Are you sure you want to log out?</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  className="flex-1 px-4 py-3 lg:py-2 rounded-md border border-gray-200 text-white hover:text-gray-600 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmLogout}
                  className="flex-1 px-4 py-3 lg:py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition shadow-sm"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Sidebar;